// Canvas renderer for the controls tray: action row (undo / erase / pencil
// / hint, each with a small caption + optional badge) and the digit row
// (1-9). Stateless — the caller passes a NumpadRenderState every paint,
// same pattern as BoardRenderer. Geometry comes from Layout.numpad; this
// module only paints.

import { roundedRect } from '@bn-games/render-helpers';
import {
  NUMPAD_ACTION_BADGE_FONT_PX,
  NUMPAD_ACTION_BADGE_HEIGHT_PX,
  NUMPAD_ACTION_BADGE_HORIZONTAL_PADDING_PX,
  NUMPAD_ACTION_BADGE_INSET_PX,
  NUMPAD_ACTION_BADGE_RADIUS_PX,
  NUMPAD_ACTION_LABEL_FONT_PX,
  NUMPAD_ACTION_LABEL_GAP_PX,
  NUMPAD_BUTTON_RADIUS_FRACTION,
  NUMPAD_DIGIT_EXHAUSTED_COUNT,
  NUMPAD_DIGIT_FONT_SCALE,
  NUMPAD_ICON_FONT_SCALE,
} from '../utils/constants.js';
import type { Digit, Grid, NonEmptyDigit } from '../game/types.js';
import { CELL_COUNT, isDigit } from '../game/types.js';
import type { NumpadButtonId, NumpadButtonRegion } from '../layout/types.js';
import type { NumpadRenderState } from './types.js';

type Ctx = CanvasRenderingContext2D;

const FONT_FAMILY = '"Segoe UI", system-ui, -apple-system, sans-serif';
const PENCIL_GLYPH = '✎';
const UNDO_GLYPH = '↶';
const HINT_GLYPH = '?';
// Eraser dimensions are derived from the same `fontPx` the text glyphs use,
// so the four action icons read at a consistent visual size across desktop
// (narrow buttons) and tablet (wide buttons). Scaling off `buttonWidth`
// instead would balloon the eraser on wide trays.
const ERASER_WIDTH_FONT_FACTOR = 1.0;
const ERASER_HEIGHT_FONT_FACTOR = 0.42;
const ERASER_BAND_FRACTION = 0.42;
const ERASER_TILT_RAD = -Math.PI / 16;
const ERASER_CORNER_RADIUS_PX = 2;

/**
 * Tallies how many times each non-empty digit appears across givens + user
 * entries. Pure function — exposed so the scene can derive the disabled
 * state for the numpad without duplicating the iteration, and so tests can
 * pin down the contract without touching a canvas.
 *
 * A digit's count saturates at NUMPAD_DIGIT_EXHAUSTED_COUNT (9) — that's the
 * threshold the renderer compares against, and no legitimate board state
 * exceeds it.
 */
export function computeDigitCounts(
  givens: Grid,
  user: Grid,
): Record<NonEmptyDigit, number> {
  const counts: Record<NonEmptyDigit, number> = {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0,
  };
  for (let i = 0; i < CELL_COUNT; i += 1) {
    const digit = pickPlacedDigit(givens, user, i);
    if (digit === 0) continue;
    counts[digit] += 1;
  }
  return counts;
}

function pickPlacedDigit(givens: Grid, user: Grid, i: number): Digit {
  const given = givens[i] ?? 0;
  if (given !== 0) return given;
  return (user[i] ?? 0) as Digit;
}

function isActionButton(id: NumpadButtonId): boolean {
  return id === 'undo' || id === 'erase' || id === 'pencil' || id === 'hint';
}

export class NumpadRenderer {
  // The tray well + frame are drawn by SceneRender; this renderer only
  // paints the buttons on top of the recessed surface.
  draw(ctx: Ctx, state: NumpadRenderState): void {
    ctx.save();
    for (const button of state.layout.numpad.buttons) {
      drawButton(ctx, state, button);
    }
    ctx.restore();
  }
}

function drawButton(
  ctx: Ctx,
  state: NumpadRenderState,
  button: NumpadButtonRegion,
): void {
  if (isActionButton(button.id)) {
    drawActionButton(ctx, state, button);
    return;
  }
  drawDigitButton(ctx, state, button);
}

interface ButtonVisualState {
  readonly active: boolean;
  readonly pressed: boolean;
  readonly disabled: boolean;
}

function pickButtonFill(
  theme: NumpadRenderState['theme'],
  vs: ButtonVisualState,
): string {
  if (vs.disabled) return theme.numpadButtonDisabled;
  if (vs.pressed) return theme.numpadButtonPressed;
  if (vs.active) return theme.numpadButtonActive;
  return theme.numpadButton;
}

function isButtonActive(state: NumpadRenderState, id: NumpadButtonId): boolean {
  if (id === 'pencil') return state.pencilMode;
  if (state.highlightDigit === null) return false;
  return id === `digit-${state.highlightDigit}`;
}

function isDigitExhausted(state: NumpadRenderState, id: NumpadButtonId): boolean {
  const digit = digitOfButton(id);
  if (digit === null) return false;
  return state.digitCounts[digit] >= NUMPAD_DIGIT_EXHAUSTED_COUNT;
}

function digitOfButton(id: NumpadButtonId): NonEmptyDigit | null {
  if (!id.startsWith('digit-')) return null;
  const raw = Number.parseInt(id.slice('digit-'.length), 10);
  return isDigit(raw) ? raw : null;
}

function drawDigitButton(
  ctx: Ctx,
  state: NumpadRenderState,
  button: NumpadButtonRegion,
): void {
  const { theme } = state;
  const radius = Math.max(4, Math.floor(button.width * NUMPAD_BUTTON_RADIUS_FRACTION));
  const disabled = isDigitExhausted(state, button.id);
  const active = !disabled && isButtonActive(state, button.id);
  const pressed = !disabled && state.pressedButton === button.id;

  ctx.fillStyle = pickButtonFill(theme, { active, pressed, disabled });
  roundedRect(ctx, button.x, button.y, button.width, button.height, radius);
  ctx.fill();

  const digit = digitOfButton(button.id);
  if (digit === null) return;
  const fontPx = Math.floor(button.height * NUMPAD_DIGIT_FONT_SCALE);
  ctx.fillStyle = disabled ? theme.numpadDigitDisabled : theme.numpadDigit;
  ctx.font = `700 ${fontPx}px ${FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(digit), button.x + button.width / 2, button.y + button.height / 2);
}

function drawActionButton(
  ctx: Ctx,
  state: NumpadRenderState,
  button: NumpadButtonRegion,
): void {
  const { theme } = state;
  // Action buttons reserve the bottom strip for the caption text and the
  // top square for the icon. The fill rect covers the icon strip only — the
  // caption sits on the tray well below it for a cleaner stacked look.
  const iconHeight = Math.max(
    0,
    button.height - NUMPAD_ACTION_LABEL_GAP_PX - NUMPAD_ACTION_LABEL_FONT_PX,
  );
  const radius = Math.max(4, Math.floor(iconHeight * NUMPAD_BUTTON_RADIUS_FRACTION));
  const active = isButtonActive(state, button.id);
  const pressed = state.pressedButton === button.id;
  ctx.fillStyle = pickButtonFill(theme, { active, pressed, disabled: false });
  roundedRect(ctx, button.x, button.y, button.width, iconHeight, radius);
  ctx.fill();

  drawActionGlyph(ctx, button, iconHeight, theme);
  drawActionCaption(ctx, button, iconHeight, captionFor(state, button.id), theme);
  drawActionBadge(ctx, button, iconHeight, state);
}

function captionFor(state: NumpadRenderState, id: NumpadButtonId): string {
  const labels = state.actionLabels;
  switch (id) {
    case 'undo': return labels.undo;
    case 'erase': return labels.erase;
    case 'pencil': return labels.pencil;
    case 'hint': return labels.hint;
    default: return '';
  }
}

function drawActionGlyph(
  ctx: Ctx,
  button: NumpadButtonRegion,
  iconHeight: number,
  theme: NumpadRenderState['theme'],
): void {
  const fontPx = Math.floor(iconHeight * NUMPAD_ICON_FONT_SCALE);
  ctx.fillStyle = theme.numpadDigit;
  ctx.font = `500 ${fontPx}px ${FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = button.x + button.width / 2;
  const cy = button.y + iconHeight / 2;
  switch (button.id) {
    case 'undo':
      ctx.fillText(UNDO_GLYPH, cx, cy);
      return;
    case 'pencil':
      ctx.fillText(PENCIL_GLYPH, cx, cy);
      return;
    case 'hint':
      ctx.fillText(HINT_GLYPH, cx, cy);
      return;
    case 'erase':
      drawEraserIcon(ctx, button, iconHeight, theme);
      return;
  }
}

function drawActionCaption(
  ctx: Ctx,
  button: NumpadButtonRegion,
  iconHeight: number,
  caption: string,
  theme: NumpadRenderState['theme'],
): void {
  if (caption === '') return;
  ctx.fillStyle = theme.numpadDigit;
  ctx.font = `500 ${NUMPAD_ACTION_LABEL_FONT_PX}px ${FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const cx = button.x + button.width / 2;
  const cy = button.y + iconHeight + NUMPAD_ACTION_LABEL_GAP_PX;
  ctx.fillText(caption, cx, cy, button.width);
}

function drawActionBadge(
  ctx: Ctx,
  button: NumpadButtonRegion,
  iconHeight: number,
  state: NumpadRenderState,
): void {
  const text = badgeFor(state, button.id);
  if (text === null) return;
  ctx.save();
  ctx.font = `700 ${NUMPAD_ACTION_BADGE_FONT_PX}px ${FONT_FAMILY}`;
  const metrics = ctx.measureText(text);
  const w = Math.max(
    NUMPAD_ACTION_BADGE_HEIGHT_PX,
    Math.ceil(metrics.width) + NUMPAD_ACTION_BADGE_HORIZONTAL_PADDING_PX * 2,
  );
  const h = NUMPAD_ACTION_BADGE_HEIGHT_PX;
  const x = button.x + button.width - w - NUMPAD_ACTION_BADGE_INSET_PX;
  const y = button.y + NUMPAD_ACTION_BADGE_INSET_PX;
  // Don't let a wide badge spill past the bottom of the icon strip.
  if (y + h > button.y + iconHeight) {
    ctx.restore();
    return;
  }
  ctx.fillStyle = state.theme.numpadButtonActive;
  roundedRect(ctx, x, y, w, h, NUMPAD_ACTION_BADGE_RADIUS_PX);
  ctx.fill();
  ctx.fillStyle = state.theme.numpadDigit;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + w / 2, y + h / 2);
  ctx.restore();
}

function badgeFor(state: NumpadRenderState, id: NumpadButtonId): string | null {
  // Pencil mode has its own visible "active" state on the button face
  // (numpadButtonActive fill when toggled on); the explicit ON/OFF badge is
  // redundant and crowds the icon. Hint keeps its count badge so the player
  // can see hints used at a glance.
  if (id === 'hint' && state.hintsUsed > 0) return String(state.hintsUsed);
  return null;
}

/** Tilted block "eraser" — a single-color filled rounded rect with a thin
 *  cross-band so it reads as a school eraser. Painted in `theme.numpadDigit`
 *  so it sits in the same color family as the pencil / undo / hint glyphs. */
function drawEraserIcon(
  ctx: Ctx,
  button: NumpadButtonRegion,
  iconHeight: number,
  theme: NumpadRenderState['theme'],
): void {
  const cx = button.x + button.width / 2;
  const cy = button.y + iconHeight / 2;
  const fontPx = Math.floor(iconHeight * NUMPAD_ICON_FONT_SCALE);
  const w = Math.floor(fontPx * ERASER_WIDTH_FONT_FACTOR);
  const h = Math.floor(fontPx * ERASER_HEIGHT_FONT_FACTOR);
  const r = ERASER_CORNER_RADIUS_PX;
  const bandY = -h / 2 + Math.floor(h * ERASER_BAND_FRACTION);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(ERASER_TILT_RAD);
  ctx.fillStyle = theme.numpadDigit;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(-w / 2, -h / 2, w, h, r);
  else ctx.rect(-w / 2, -h / 2, w, h);
  ctx.fill();
  // Punch a 1px hairline through the body so the icon reads as eraser not
  // a flat block.
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = '#000';
  ctx.fillRect(-w / 2 + r, bandY, w - r * 2, 1);
  ctx.restore();
}
